import { Component, computed, effect, input, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipDirective } from 'shared/directives/tooltip.directive';

export interface HorizontalBarChartBar {
    label: string;
    value: number;
    targetValue: number;
    color: string;
    tooltip?: string;
    isCombinedBar?: boolean;
}

interface ComputedBar extends HorizontalBarChartBar {
    widthPercent: number;
    tooltip: string;
}

@Component({
    selector: 'app-ice-visuals-dashboard-horizontal-bar-chart',
    imports: [TooltipDirective, TranslateModule],
    templateUrl: './ice-visuals-dashboard-horizontal-bar-chart.component.html',
    styleUrl: './ice-visuals-dashboard-horizontal-bar-chart.component.scss',
})
export class IceVisualsDashboardHorizontalBarChartComponent {
    bars = input.required<HorizontalBarChartBar[]>();
    loading = input.required<boolean>();
    scoreLabel = input.required<string>();
    targetLabel = input.required<string>();

    isAnimated = signal(false);
    displayValues = signal<number[]>([]);

    onLangChange = toSignal(this.translateService.onLangChange);

    maxValue = computed(() => Math.max(...this.bars().map((bar) => Math.max(bar.value, bar.targetValue))));

    computedBars = computed<ComputedBar[]>(() => {
        const max = this.maxValue();
        this.onLangChange(); // Recompute on language change for tooltips
        return this.bars().map((bar) => ({
            ...bar,
            widthPercent: (bar.value / max) * 100,
            tooltip: bar.tooltip ?? this.buildTooltip(bar),
        }));
    });

    animatedBars = computed(() => {
        const animated = this.isAnimated();
        const displayVals = this.displayValues();

        return this.computedBars().map((bar, index) => ({
            ...bar,
            animatedWidth: animated ? bar.widthPercent : 0,
            displayValue: displayVals[index] ?? 0,
        }));
    });

    skeletonRows = [1, 2, 3, 4];

    constructor(private translateService: TranslateService) {
        effect(() => {
            const isLoading = this.loading();
            if (!isLoading) {
                untracked(() => {
                    this.isAnimated.set(false);
                    this.displayValues.set(this.bars().map(() => 0));
                    setTimeout(() => {
                        this.isAnimated.set(true);
                        this.animateValues();
                    }, 100);
                });
            }
        });
    }

    private buildTooltip(bar: HorizontalBarChartBar): string {
        const percentage = Math.round((bar.value / bar.targetValue) * 100);
        return `${this.translateService.instant(bar.label)}: ${bar.value} / ${bar.targetValue} (${percentage}%)`;
    }

    private animateValues(): void {
        const targets = this.bars().map((bar) => bar.value);
        const duration = 800;
        const startTime = performance.now();

        const animate = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);

            this.displayValues.set(targets.map((target) => Math.round(eased * target)));

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);
    }
}
